import { FC, useEffect } from 'react';

import { useEntitySchema, Utils, useStack9 } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import { Divider, Row, Col, Form as AntForm, message, Typography } from 'antd';
import dayjs from 'dayjs';
import { z } from 'zod';

import { Event as S9Event } from '../../models/stack9/Event';
import {
  getAfterDateRule,
  getSameOrBeforeDateRule,
  getEndDateTimeAfterStartRule,
  getEndDateAfterOrEqualStartRule,
  getStartTimeBeforeEndRule,
} from '../../utils/validators';
import { EmailTemplateSelect } from '../EmailTemplateSelect';

const Drawer = ui.S9Drawer.default;
const Form = ui.S9Form.default;
const Button = ui.S9Button.default;
const Fieldset = ui.S9Fieldset.default;
const PageSection = ui.S9PageSection.default;
const PageTitle = ui.S9PageTitle.default;
const S9EntityActions = ui.S9EntityActions.default;
const DateField = ui.S9DateField.default;

const { useForm, useWatch } = ui.S9Form;

type Event = z.infer<typeof S9Event>;

interface EventFormProps {
  id: number | undefined;
  canEdit?: boolean;
  onSaved: () => void;
  onDeleted: () => void;
  onClose: () => void;
}

const EventForm: FC<EventFormProps> = ({
  id = 0,
  canEdit = true,
  onSaved,
  onDeleted,
  onClose,
}) => {
  const [form] = useForm();
  const { schema, isValidating } = useEntitySchema('event');
  const { handleSave, isLoading: isSaving } = ui.useEntityActions('event');
  const { config } = useStack9();

  const { data: record } = ui.useScreenQueryById<Event>('geteventbyid', id, {
    shouldFetch: !!id,
  });

  const bookingType = useWatch(['booking_type'], form);
  const isFeatured = useWatch(['is_featured'], form);
  const sendRegistrationEmail = useWatch(['send_registration_email'], form);
  const sendAppointmentEmail = useWatch(['send_appointment_email'], form);
  const sendCoordinatorEmail = useWatch(['send_coordinator_email'], form);
  const sendReminderEmail = useWatch(['send_reminder_email'], form);
  const sendReminderSms = useWatch(['send_reminder_sms'], form);
  const sendCancellationEmail = useWatch(['send_cancellation_email'], form);
  const sendCancellationSms = useWatch(['send_cancellation_sms'], form);
  const sendCancellationCoordinatorEmail = useWatch(
    ['send_cancellation_coordinator_email'],
    form,
  );
  const startDate = useWatch(['start_date'], form);
  const startTime = useWatch(['start_time'], form);
  const endDate = useWatch(['end_date'], form);
  const endTime = useWatch(['end_time'], form);
  const dateFormat = Utils.date.normaliseFormat(
    config.globalization.dateFormat,
  );

  const initialValues = record
    ? {
        ...record,
        department_id: (record as { department?: unknown[] }).department || [],
      }
    : {
        timezone: 'Australia/Sydney',
      };

  useEffect(() => {
    form.resetFields();
  }, [form, id, record]);

  useEffect(() => {
    if (!isFeatured) {
      form.setFieldValue('featured_order', null);
    }
  }, [form, isFeatured]);

  useEffect(() => {
    const fieldsToValidate: string[] = [];

    if (endDate) {
      fieldsToValidate.push('end_date');
    }
    if (startTime) {
      fieldsToValidate.push('start_time');
    }
    if (endTime) {
      fieldsToValidate.push('end_time');
    }

    if (fieldsToValidate.length > 0) {
      form.validateFields(fieldsToValidate).catch(() => {});
    }
  }, [form, startDate, startTime, endDate, endTime]);

  return (
    <Form
      form={form}
      disabled={isSaving || isValidating || canEdit === false}
      initialValues={initialValues}
      loading={isValidating}
      onFinish={async values => {
        try {
          let eventThumbnail = values.event_thumbnail;

          if (eventThumbnail && typeof eventThumbnail !== 'string') {
            eventThumbnail = JSON.stringify(eventThumbnail);
          }

          const submitData = {
            ...values,
            start_date: values.start_date
              ? dayjs(values.start_date).toISOString()
              : null,
            end_date: values.end_date
              ? dayjs(values.end_date).toISOString()
              : null,
            publish_date: values.publish_date
              ? dayjs(values.publish_date).toISOString()
              : null,
            retire_date: values.retire_date
              ? dayjs(values.retire_date).toISOString()
              : null,
            registration_cutoff_date: values.registration_cutoff_date
              ? dayjs(values.registration_cutoff_date).toISOString()
              : null,
            appointment_cutoff_date: values.appointment_cutoff_date
              ? dayjs(values.appointment_cutoff_date).toISOString()
              : null,
            event_thumbnail: eventThumbnail,
            department_id: (values.department_id ?? []).map(
              (dept: { id: number } | number) =>
                typeof dept === 'number' ? dept : dept.id,
            ),
            topics: (values.topics ?? []).map(
              (topic: { id: number } | number) =>
                typeof topic === 'number' ? topic : topic.id,
            ),
          };

          await handleSave(submitData, id);

          onSaved();
          onClose();
        } catch (error: unknown) {
          const errorMessage =
            (
              error as {
                response?: { data?: { error?: string; errorMessage?: string } };
              }
            )?.response?.data?.error ||
            (
              error as {
                response?: { data?: { error?: string; errorMessage?: string } };
              }
            )?.response?.data?.errorMessage ||
            (error as { message?: string })?.message ||
            'An error occurred while saving the event';

          message.error(errorMessage);
        }
      }}
    >
      <Drawer.Header
        title={id ? `Update event #${id}` : 'Create event'}
        extra={
          <>
            <Button label="Cancel" onClick={onClose} />
            <S9EntityActions
              form={form}
              entityKey="event"
              entityId={undefined}
              disabled={isSaving}
              isSaving={isSaving}
              onDeleted={onDeleted}
              filterPrintableDoc={() => false}
            />
          </>
        }
      />
      <Drawer.Content>
        {schema && (
          <>
            <PageSection title="Basic Information" size="sm">
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[{ field: 'event_name' }]}
              />
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[
                  { field: 'slug', colSize: 12 },
                  { field: 'event_type', colSize: 6 },
                  { field: 'booking_type', colSize: 6 },
                ]}
              />
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[{ field: 'event_description' }]}
              />
            </PageSection>

            <PageSection title="Date & Time" size="sm">
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[
                  { field: 'start_date', colSize: 12 },
                  {
                    field: 'end_date',
                    colSize: 12,
                    rules: [getEndDateAfterOrEqualStartRule()],
                  },
                  {
                    field: 'start_time',
                    colSize: 12,
                    rules: [getStartTimeBeforeEndRule()],
                  },
                  {
                    field: 'end_time',
                    colSize: 12,
                    rules: [getEndDateTimeAfterStartRule()],
                  },
                  { field: 'timezone', colSize: 12 },
                ]}
              />
              <Row gutter={16}>
                <Col span={12}>
                  <DateField
                    label="Publish Date"
                    name="publish_date"
                    format={dateFormat}
                    time
                    rules={[
                      { required: true, message: 'Publish date is required' },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <DateField
                    label="Retire Date"
                    name="retire_date"
                    format={dateFormat}
                    time
                    rules={[getAfterDateRule('publish_date', 'Publish Date')]}
                  />
                </Col>
              </Row>
              {bookingType === 'Register' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <DateField
                      label="Registration Cutoff Date"
                      name="registration_cutoff_date"
                      format={dateFormat}
                      time
                      rules={[
                        {
                          required: true,
                          message: 'Registration cutoff date is required',
                        },
                        getSameOrBeforeDateRule('start_date', 'Start Date'),
                      ]}
                    />
                  </Col>
                </Row>
              )}
              {bookingType === 'Appointment' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <DateField
                      label="Appointment Cutoff Date"
                      name="appointment_cutoff_date"
                      format={dateFormat}
                      time
                      rules={[
                        {
                          required: true,
                          message: 'Appointment cutoff date is required',
                        },
                        getSameOrBeforeDateRule('start_date', 'Start Date'),
                      ]}
                    />
                  </Col>
                </Row>
              )}
            </PageSection>

            {bookingType === 'Appointment' && (
              <PageSection title="Appointment Settings" size="sm">
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'slot_duration_minutes',
                      colSize: 8,
                      rules: [
                        {
                          required: true,
                          message:
                            'Slot duration is required for appointment events',
                        },
                      ],
                    },
                    {
                      field: 'slot_capacity',
                      colSize: 8,
                      rules: [
                        {
                          required: true,
                          message:
                            'Slot capacity is required for appointment events',
                        },
                      ],
                    },
                    { field: 'max_capacity', colSize: 8 },
                    { field: 'total_bookings', colSize: 8 },
                    { field: 'available_spaces', colSize: 8 },
                  ]}
                />
              </PageSection>
            )}

            {bookingType === 'Register' && (
              <PageSection title="Registration Settings" size="sm">
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    { field: 'max_capacity', colSize: 8 },
                    { field: 'total_bookings', colSize: 8 },
                    { field: 'available_spaces', colSize: 8 },
                  ]}
                />
              </PageSection>
            )}

            {bookingType === 'Webinar' && (
              <PageSection title="Webinar Settings" size="sm">
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'webinar_url',
                      colSize: 12,
                      rules: [
                        {
                          required: true,
                          message: 'Webinar URL is required for webinar events',
                        },
                      ],
                    },
                  ]}
                />
              </PageSection>
            )}

            <PageSection title="Location & Contact" size="sm">
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[
                  { field: 'location', colSize: 12 },
                  { field: 'region', colSize: 12 },
                  { field: 'event_coordinator', colSize: 12 },
                  { field: 'coordinator_email', colSize: 12 },
                ]}
              />
            </PageSection>

            {(bookingType === 'Register' || bookingType === 'Appointment') && (
              <PageSection title="Notifications" size="sm">
                {bookingType === 'Register' && (
                  <>
                    <PageTitle
                      level={4}
                      title="Registration Email"
                      className="mt-4"
                    />
                    <Divider className="my-3" />
                    <Row gutter={16}>
                      <Col span={12}>
                        <AntForm.Item
                          label="Registration Email Template"
                          name="registration_email_template"
                          tooltip="Email template ID for registration confirmation (required if Send Registration Email is enabled)"
                          rules={
                            sendRegistrationEmail
                              ? [
                                  {
                                    required: true,
                                    message:
                                      'Registration email template is required when email notifications are enabled',
                                  },
                                ]
                              : []
                          }
                        >
                          <EmailTemplateSelect placeholder="Select registration email template" type="event-bookings" />
                        </AntForm.Item>
                      </Col>
                      <Col span={12}>
                        <Fieldset
                          context={{ id: undefined }}
                          schema={schema}
                          formFields={[
                            {
                              field: 'registration_email_subject',
                              rules: sendRegistrationEmail
                                ? [
                                    {
                                      required: true,
                                      message:
                                        'Registration email subject is required when email notifications are enabled',
                                    },
                                  ]
                                : [],
                            },
                          ]}
                        />
                      </Col>
                    </Row>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_registration_email' }]}
                    />

                    <PageTitle
                      level={4}
                      title="Registration SMS"
                      className="mt-4"
                    />
                    <Divider className="my-3" />
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'registration_sms_template' }]}
                    />
                    <Typography.Text
                      type="secondary"
                      style={{
                        display: 'block',
                        marginTop: -16,
                        marginBottom: 16,
                        fontSize: '12px',
                      }}
                    >
                      Use double braces for variables (e.g., {`{{event.event_name}}`}, {`{{event.start_date}}`}, {`{{event.location}}`}, {`{{user.full_name}}`}, {`{{booking.booking_reference}}`}, {`{{event.id}}`}, {`{{booking.id}}`}, etc.)
                    </Typography.Text>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_registration_sms' }]}
                    />
                  </>
                )}

                {bookingType === 'Appointment' && (
                  <>
                    <PageTitle
                      level={4}
                      title="Appointment Email"
                      className="mt-4"
                    />
                    <Divider className="my-3" />
                    <Row gutter={16}>
                      <Col span={12}>
                        <AntForm.Item
                          label="Appointment Email Template"
                          name="appointment_email_template"
                          tooltip="Email template ID for appointment confirmation (required if Send Appointment Email is enabled)"
                          rules={
                            sendAppointmentEmail
                              ? [
                                  {
                                    required: true,
                                    message:
                                      'Appointment email template is required when email notifications are enabled',
                                  },
                                ]
                              : []
                          }
                        >
                          <EmailTemplateSelect placeholder="Select appointment email template" type="event-bookings" />
                        </AntForm.Item>
                      </Col>
                      <Col span={12}>
                        <Fieldset
                          context={{ id: undefined }}
                          schema={schema}
                          formFields={[
                            {
                              field: 'appointment_email_subject',
                              rules: sendAppointmentEmail
                                ? [
                                    {
                                      required: true,
                                      message:
                                        'Appointment email subject is required when email notifications are enabled',
                                    },
                                  ]
                                : [],
                            },
                          ]}
                        />
                      </Col>
                    </Row>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_appointment_email' }]}
                    />

                    <PageTitle
                      level={4}
                      title="Appointment SMS"
                      className="mt-4"
                    />
                    <Divider className="my-3" />
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'appointment_sms_template' }]}
                    />
                    <Typography.Text
                      type="secondary"
                      style={{
                        display: 'block',
                        marginTop: -16,
                        marginBottom: 16,
                        fontSize: '12px',
                      }}
                    >
                      Use double braces for variables (e.g., {`{{event.event_name}}`}, {`{{event.start_date}}`}, {`{{event.start_time}}`}, {`{{event.location}}`}, {`{{user.full_name}}`}, {`{{slot.start_dt}}`}, {`{{slot.end_dt}}`}, {`{{booking.booking_reference}}`}, etc.)
                    </Typography.Text>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_appointment_sms' }]}
                    />
                  </>
                )}

                <PageTitle
                  level={4}
                  title="Coordinator Email"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Row gutter={16}>
                  <Col span={12}>
                    <AntForm.Item
                      label="Coordinator Email Template"
                      name="coordinator_email_template"
                      tooltip="Email template ID for coordinator notification (required if Send Coordinator Email is enabled)"
                      rules={
                        sendCoordinatorEmail
                          ? [
                              {
                                required: true,
                                message:
                                  'Coordinator email template is required when coordinator notifications are enabled',
                              },
                            ]
                          : []
                      }
                    >
                      <EmailTemplateSelect placeholder="Select coordinator email template" type="event-bookings" />
                    </AntForm.Item>
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[
                        {
                          field: 'coordinator_email_subject',
                          rules: sendCoordinatorEmail
                            ? [
                                {
                                  required: true,
                                  message:
                                    'Coordinator email subject is required when coordinator notifications are enabled',
                                },
                              ]
                            : [],
                        },
                      ]}
                    />
                  </Col>
                </Row>
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'send_coordinator_email' }]}
                />

                <PageTitle
                  level={4}
                  title="Event Reminder Email"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Row gutter={16}>
                  <Col span={12}>
                    <AntForm.Item
                      label="Reminder Email Template"
                      name="reminder_email_template"
                      tooltip="Email template ID for event reminder (required if Send Reminder Email is enabled)"
                      rules={
                        sendReminderEmail
                          ? [
                              {
                                required: true,
                                message:
                                  'Reminder email template is required when reminder emails are enabled',
                              },
                            ]
                          : []
                      }
                    >
                      <EmailTemplateSelect placeholder="Select reminder email template" type="event-bookings" />
                    </AntForm.Item>
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[
                        {
                          field: 'reminder_email_subject',
                          rules: sendReminderEmail
                            ? [
                                {
                                  required: true,
                                  message:
                                    'Reminder email subject is required when reminder emails are enabled',
                                },
                              ]
                            : [],
                        },
                      ]}
                    />
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'reminder_email_hours_before' }]}
                    />
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_reminder_email' }]}
                    />
                  </Col>
                </Row>

                <PageTitle
                  level={4}
                  title="Event Reminder SMS"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'reminder_sms_template',
                      rules: sendReminderSms
                        ? [
                            {
                              required: true,
                              message:
                                'Reminder SMS template is required when reminder SMS is enabled',
                            },
                          ]
                        : [],
                    },
                  ]}
                />
                <Typography.Text
                  type="secondary"
                  style={{
                    display: 'block',
                    marginTop: -16,
                    marginBottom: 16,
                    fontSize: '12px',
                  }}
                >
                  Use double braces for variables (e.g., {`{{event.event_name}}`}, {`{{event.start_date}}`}, {`{{event.start_time}}`}, {`{{event.location}}`}, {`{{user.full_name}}`}, {`{{booking.booking_reference}}`}, etc.)
                </Typography.Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'reminder_sms_hours_before' }]}
                    />
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[{ field: 'send_reminder_sms' }]}
                    />
                  </Col>
                </Row>

                <PageTitle
                  level={4}
                  title="Cancellation Notification Email"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Row gutter={16}>
                  <Col span={12}>
                    <AntForm.Item
                      label="Cancellation Email Template"
                      name="cancellation_email_template"
                      tooltip="Email template ID for booking cancellation (required if Send Cancellation Email is enabled)"
                      rules={
                        sendCancellationEmail
                          ? [
                              {
                                required: true,
                                message:
                                  'Cancellation email template is required when cancellation emails are enabled',
                              },
                            ]
                          : []
                      }
                    >
                      <EmailTemplateSelect placeholder="Select cancellation email template" type="event-bookings" />
                    </AntForm.Item>
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[
                        {
                          field: 'cancellation_email_subject',
                          rules: sendCancellationEmail
                            ? [
                                {
                                  required: true,
                                  message:
                                    'Cancellation email subject is required when cancellation emails are enabled',
                                },
                              ]
                            : [],
                        },
                      ]}
                    />
                  </Col>
                </Row>
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'send_cancellation_email' }]}
                />

                <PageTitle
                  level={4}
                  title="Cancellation Notification SMS"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'cancellation_sms_template',
                      rules: sendCancellationSms
                        ? [
                            {
                              required: true,
                              message:
                                'Cancellation SMS template is required when cancellation SMS is enabled',
                            },
                          ]
                        : [],
                    },
                  ]}
                />
                <Typography.Text
                  type="secondary"
                  style={{
                    display: 'block',
                    marginTop: -16,
                    marginBottom: 16,
                    fontSize: '12px',
                  }}
                >
                  Use double braces for variables (e.g., {`{{event.event_name}}`}, {`{{event.start_date}}`}, {`{{user.full_name}}`}, {`{{booking.booking_reference}}`}, {`{{booking.cancelled_at}}`}, etc.)
                </Typography.Text>
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'send_cancellation_sms' }]}
                />

                <PageTitle
                  level={4}
                  title="Cancellation Coordinator Email"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Row gutter={16}>
                  <Col span={12}>
                    <AntForm.Item
                      label="Cancellation Coordinator Email Template"
                      name="cancellation_coordinator_email_template"
                      tooltip="Email template for coordinator when booking is cancelled (required if Send Cancellation Coordinator Email is enabled)"
                      rules={
                        sendCancellationCoordinatorEmail
                          ? [
                              {
                                required: true,
                                message:
                                  'Cancellation coordinator email template is required when coordinator cancellation notifications are enabled',
                              },
                            ]
                          : []
                      }
                    >
                      <EmailTemplateSelect placeholder="Select cancellation coordinator email template" type="event-bookings" />
                    </AntForm.Item>
                  </Col>
                  <Col span={12}>
                    <Fieldset
                      context={{ id: undefined }}
                      schema={schema}
                      formFields={[
                        {
                          field: 'cancellation_coordinator_email_subject',
                          rules: sendCancellationCoordinatorEmail
                            ? [
                                {
                                  required: true,
                                  message:
                                    'Cancellation coordinator email subject is required when coordinator cancellation notifications are enabled',
                                },
                              ]
                            : [],
                        },
                      ]}
                    />
                  </Col>
                </Row>
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[
                    { field: 'send_cancellation_coordinator_email' },
                  ]}
                />
              </PageSection>
            )}

            {(bookingType === 'Register' || bookingType === 'Appointment') && (
              <PageSection title="Consent & Waiver" size="sm">
                <PageTitle
                  level={4}
                  title="Consent Settings"
                  className="mt-4"
                />
                <Divider className="my-3" />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'consent_text' }]}
                />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'requires_consent' }]}
                />

                <PageTitle level={4} title="Waiver Settings" className="mt-4" />
                <Divider className="my-3" />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'waiver_text' }]}
                />
                <Fieldset
                  context={{ id: undefined }}
                  schema={schema}
                  formFields={[{ field: 'requires_waiver' }]}
                />
              </PageSection>
            )}

            <PageSection title="Organization & Display" size="sm">
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[{ field: 'department_id' }, { field: 'topics' }]}
              />

              <PageTitle level={4} title="Event Thumbnail" className="mt-4" />
              <Divider className="my-3" />
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[{ field: 'event_thumbnail' }]}
              />

              <PageTitle level={4} title="Featured Settings" className="mt-4" />
              <Divider className="my-3" />
              <Fieldset
                context={{ id: undefined }}
                schema={schema}
                formFields={[
                  { field: 'is_featured', colSize: 12 },
                  {
                    field: 'featured_order',
                    colSize: 12,
                    rules: isFeatured
                      ? [
                          {
                            required: true,
                            message:
                              'Featured order is required when event is featured',
                          },
                        ]
                      : [],
                  },
                ]}
              />
            </PageSection>
          </>
        )}
      </Drawer.Content>
    </Form>
  );
};

interface EventDrawerProps {
  open: boolean;
  id?: number | undefined;
  canEdit?: boolean;
  onSaved?: () => void;
  onDeleted?: () => void;
  onClose: () => void;
}

export const EventDrawer: FC<EventDrawerProps> = ({
  open,
  id = undefined,
  canEdit = true,
  onSaved = () => {},
  onDeleted = () => {},
  onClose,
}) => (
  <Drawer
    open={open}
    closable
    keyboard={false}
    maskClosable={false}
    onClose={onClose}
    width={880}
  >
    <EventForm
      id={id}
      canEdit={canEdit}
      onSaved={onSaved}
      onDeleted={onDeleted}
      onClose={onClose}
    />
  </Drawer>
);
